import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { IoArrowForward, IoArrowBack } from 'react-icons/io5';
import { useDarkMode } from '../../context/DarkModeContext';
import SortableItem from './SortableItem';
import { FilterItem } from './types';

interface FilterPanelsProps {
  availableItems: {
    notes: FilterItem[];
    cards: FilterItem[];
  };
  selectedItems: {
    notes: FilterItem[];
    cards: FilterItem[];
  };
  onItemSelect: (items: FilterItem[]) => void;
  onItemDeselect: (items: FilterItem[]) => void;
  onMoveAllRight: () => void;
  onMoveAllLeft: () => void;
  searchQuery: string;
  selectedTags: string[];
  getItemKey: (item: FilterItem) => string;
}

const FilterPanels: React.FC<FilterPanelsProps> = ({
  availableItems,
  selectedItems,
  onItemSelect,
  onItemDeselect,
  onMoveAllRight,
  onMoveAllLeft,
  searchQuery,
  selectedTags,
  getItemKey
}) => {
  const { darkMode } = useDarkMode();
  const [selectedAvailableItems, setSelectedAvailableItems] = useState<Set<string>>(new Set());
  const [selectedRightItems, setSelectedRightItems] = useState<Set<string>>(new Set());
  const lastClickedAvailableRef = useRef<string | null>(null);
  const lastClickedSelectedRef = useRef<string | null>(null);

  const handleItemSelection = (
    items: FilterItem[],
    itemKey: string,
    isShiftKey: boolean,
    selectedSet: Set<string>,
    setSelectedSet: React.Dispatch<React.SetStateAction<Set<string>>>,
    lastClickedRef: React.MutableRefObject<string | null>
  ) => {
    if (!isShiftKey || !lastClickedRef.current) {
      // Normal click behavior
      setSelectedSet(prev => {
        const newSet = new Set(prev);
        if (newSet.has(itemKey)) {
          newSet.delete(itemKey);
        } else {
          newSet.add(itemKey);
        }
        return newSet;
      });
      lastClickedRef.current = itemKey;
    } else {
      // Shift-click behavior
      const filteredItems = getFilteredItems(items);
      const currentIndex = filteredItems.findIndex(item => getItemKey(item) === itemKey);
      const lastClickedIndex = filteredItems.findIndex(item => getItemKey(item) === lastClickedRef.current);
      
      if (currentIndex !== -1 && lastClickedIndex !== -1) {
        const start = Math.min(currentIndex, lastClickedIndex);
        const end = Math.max(currentIndex, lastClickedIndex);
        
        const itemsToSelect = filteredItems.slice(start, end + 1);
        setSelectedSet(prev => {
          const newSet = new Set(prev);
          itemsToSelect.forEach(item => newSet.add(getItemKey(item)));
          return newSet;
        });
      }
      lastClickedRef.current = itemKey;
    }
  };

  const handleAvailableItemClick = (item: FilterItem, event: React.MouseEvent<HTMLButtonElement>) => {
    const itemKey = getItemKey(item);
    handleItemSelection(
      [...availableItems.notes, ...availableItems.cards],
      itemKey,
      event.shiftKey,
      selectedAvailableItems,
      setSelectedAvailableItems,
      lastClickedAvailableRef
    );
  };

  const handleSelectedItemClick = (item: FilterItem, event: React.MouseEvent<HTMLButtonElement>) => {
    const itemKey = getItemKey(item);
    handleItemSelection(
      [...selectedItems.notes, ...selectedItems.cards],
      itemKey,
      event.shiftKey,
      selectedRightItems,
      setSelectedRightItems,
      lastClickedSelectedRef
    );
  };

  const handleMoveSelectedRight = () => {
    const itemsToMove = [...availableItems.notes, ...availableItems.cards]
      .filter(item => selectedAvailableItems.has(getItemKey(item)));
    onItemSelect(itemsToMove);
    setSelectedAvailableItems(new Set());
  };

  const handleMoveSelectedLeft = () => {
    const itemsToMove = [...selectedItems.notes, ...selectedItems.cards]
      .filter(item => selectedRightItems.has(getItemKey(item)));
    onItemDeselect(itemsToMove);
    setSelectedRightItems(new Set());
  };

  const getFilteredItems = (items: FilterItem[]) => {
    return items.filter(item => {
      const matchesSearch = !searchQuery || 
        item.title.toLowerCase().includes(searchQuery.toLowerCase());
      const itemTags = item.type === 'note'
        ? [...(item.custom_tags || []), ...(item.date_tags || [])]
        : [...(item.card_data?.custom_tags || []), ...(item.card_data?.date_tags || [])];
      const matchesTags = selectedTags.length === 0 ||
        selectedTags.every(tag => itemTags.includes(tag));
      return matchesSearch && matchesTags;
    });
  };

  const handleSelectAllAvailable = () => {
    if (selectedAvailableItems.size > 0) {
      setSelectedAvailableItems(new Set());
    } else {
      const filteredItems = getFilteredItems([...availableItems.notes, ...availableItems.cards]);
      const availableKeys = filteredItems.map(item => getItemKey(item));
      setSelectedAvailableItems(new Set(availableKeys));
    }
  };

  const handleSelectAllSelected = () => {
    if (selectedRightItems.size > 0) {
      setSelectedRightItems(new Set());
    } else {
      const filteredItems = getFilteredItems([...selectedItems.notes, ...selectedItems.cards]);
      const selectedKeys = filteredItems.map(item => getItemKey(item));
      setSelectedRightItems(new Set(selectedKeys));
    }
  };

  const renderItemList = (
    items: FilterItem[],
    onItemClick: (item: FilterItem, event: React.MouseEvent<HTMLButtonElement>) => void,
    selectedSet: Set<string>
  ) => {
    const filteredItems = getFilteredItems(items);
    return (
      <div className="space-y-1">
        {filteredItems.map((item) => (
          <SortableItem
            key={getItemKey(item)}
            id={item.id}
            item={item}
            isSelected={selectedSet.has(getItemKey(item))}
            darkMode={darkMode}
            onClick={(event) => onItemClick(item, event)}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex gap-4 h-[400px]">
      {/* Available Items Panel */}
      <div className={`flex-1 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className="flex justify-between items-center mb-3">
          <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Available Items ({getFilteredItems([...availableItems.notes, ...availableItems.cards]).length})
          </h3>
          <div className="flex gap-1">
            <button
              onClick={handleMoveSelectedRight}
              disabled={selectedAvailableItems.size === 0}
              className={`p-1.5 rounded ${
                darkMode 
                  ? 'text-gray-200 hover:bg-gray-600/10' 
                  : 'text-gray-700 hover:bg-gray-100'
              } ${selectedAvailableItems.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Move selected items to right"
            >
              <IoArrowForward className="w-4 h-4" />
            </button>
            <button
              onClick={handleSelectAllAvailable}
              className={`text-xs px-2 py-1 rounded ${
                darkMode 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-600' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={selectedAvailableItems.size > 0 ? "Deselect all available items" : "Select all available items"}
            >
              {selectedAvailableItems.size > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
        <div className={`overflow-y-auto h-[calc(100%-2.5rem)] scrollbar-thin ${
          darkMode
            ? 'scrollbar-thumb-gray-600 scrollbar-hover:scrollbar-thumb-gray-500'
            : 'scrollbar-thumb-gray-200 scrollbar-hover:scrollbar-thumb-gray-300'
        } scrollbar-track-transparent`}>
          {renderItemList(
            [...availableItems.notes, ...availableItems.cards],
            handleAvailableItemClick,
            selectedAvailableItems
          )}
        </div>
      </div>

      {/* Selected Items Panel */}
      <div className={`flex-1 p-4 rounded-lg border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className="flex justify-between items-center mb-3">
          <div className="flex gap-1">
            <button
              onClick={handleSelectAllSelected}
              className={`text-xs px-2 py-1 rounded ${
                darkMode 
                  ? 'text-gray-300 hover:text-white hover:bg-gray-600' 
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              title={selectedRightItems.size > 0 ? "Deselect all selected items" : "Select all selected items"}
            >
              {selectedRightItems.size > 0 ? 'Deselect All' : 'Select All'}
            </button>
            <button
              onClick={handleMoveSelectedLeft}
              disabled={selectedRightItems.size === 0}
              className={`p-1.5 rounded ${
                darkMode 
                  ? 'text-gray-200 hover:bg-gray-600/10' 
                  : 'text-gray-700 hover:bg-gray-100'
              } ${selectedRightItems.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              title="Move selected items to left"
            >
              <IoArrowBack className="w-4 h-4" />
            </button>
          </div>
          <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            Selected Items ({getFilteredItems([...selectedItems.notes, ...selectedItems.cards]).length})
          </h3>
        </div>
        <div className={`overflow-y-auto h-[calc(100%-2.5rem)] scrollbar-thin ${
          darkMode
            ? 'scrollbar-thumb-gray-600 scrollbar-hover:scrollbar-thumb-gray-500'
            : 'scrollbar-thumb-gray-200 scrollbar-hover:scrollbar-thumb-gray-300'
        } scrollbar-track-transparent`}>
          {renderItemList(
            [...selectedItems.notes, ...selectedItems.cards],
            handleSelectedItemClick,
            selectedRightItems
          )}
        </div>
      </div>
    </div>
  );
};

export default FilterPanels; 